import { Input } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { useClient } from '@/stores/useClient.ts'
import { useEffect, useState } from 'react'
import { useProfile } from '@/stores/useAuth.ts'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Trans, useTranslation } from 'react-i18next'
import { captureException } from '@/controller/sentryHelper.ts'
import { trackLead } from '@/controller/tracking.ts'
import { HeyThemeConstantProvider, useHeyTheme } from '@/contexts/heyTheme.tsx'
import { PORTAL_PRIMARY, PORTAL_NEUTRAL, PORTAL_TEXT, PORTAL_BG, PORTAL_BORDER } from '@/controller/driveDefaults.ts'
import { getApiErrorCode, getApiErrorStatus } from '@/controller/apiErrors.ts'

/**
 * Both portal entry points share one flow — email first, then whatever the backend
 * decides (password field, login link, or a fresh demo account). Only the framing
 * differs, and that framing is the point: a login must look like a login, a signup
 * like a signup, and each must offer the way over to the other.
 *
 * `login` never creates an account and `signup` never refuses one, so nobody ends
 * up on a dead end: an unknown email on the login page is offered the signup, a
 * known email on the signup page is walked into the login.
 */
export type PortalAuthMode = 'login' | 'signup'

type LoginStep = 'email' | 'password' | 'link_sent'

export const PORTAL_LOGIN_PATH = '/portal/login'
export const PORTAL_SIGNUP_PATH = '/portal/demo'

export default function PortalAuthPage({ mode }: { mode: PortalAuthMode }) {
  return (
    <HeyThemeConstantProvider colorPrimary={PORTAL_PRIMARY} colorNeutral={PORTAL_NEUTRAL} colorContent={PORTAL_TEXT} font="Cormorant Infant">
      <PortalAuthPageContent mode={mode} />
    </HeyThemeConstantProvider>
  )
}

function PortalAuthPageContent({ mode }: { mode: PortalAuthMode }) {
  const { t } = useTranslation()
  const { login, createProfDemo } = useClient()
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [step, setStep] = useState<LoginStep>('email')
  // Carried over when someone is sent from one page to the other, so nobody
  // types their address twice.
  const [email, setEmail] = useState(searchParams.get('email') || '')
  const [password, setPassword] = useState('')
  const { authenticationToken, setAuthenticationToken, setProfile } = useProfile()
  // Captured once on mount, deliberately: a successful signup sets the token and
  // navigates to the onboarding in the same handler — reading the live value here
  // would redirect to the dashboard instead.
  const [wasSignedInOnArrival] = useState(() => authenticationToken != null)
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  // Only failed password attempts count towards the rate limit and the login link
  // is not affected by it — so a 429 must offer that way out right where it happens.
  const [showLinkFallback, setShowLinkFallback] = useState(false)
  // Login page, unknown email: don't just fail — offer the signup.
  const [isUnknownEmail, setIsUnknownEmail] = useState(false)
  // Signup page, known email: say so before showing a password field.
  const [hasExistingAccount, setHasExistingAccount] = useState(false)
  const { style: themeStyle } = useHeyTheme()

  const isSignup = mode === 'signup'

  useEffect(() => {
    // Trigger Cookiebot popup for demo users
    if (isSignup && window.Cookiebot) {
      window.Cookiebot.show()
    }
  }, [isSignup])

  function applyLoginSession(profile: any, haToken: string) {
    setProfile(profile)
    setAuthenticationToken(haToken)
  }

  /** Turns an auth error into a German message; returns true when it was handled. */
  function handleAuthError(e: any): boolean {
    const status = getApiErrorStatus(e)

    if (status === 429) {
      setError('Zu viele Versuche. Bitte probiere es später erneut oder lass dir einen Login-Link schicken.')
      setShowLinkFallback(true)
      return true
    }
    if (status === 403 && getApiErrorCode(e) === 'inactive_account') {
      setError('Dieser Account ist deaktiviert. Bitte melde dich bei uns.')
      return true
    }
    if (status === 401) {
      // Deliberately generic: never reveal whether the email exists.
      setError('E-Mail oder Passwort ist falsch.')
      return true
    }
    return false
  }

  function resetMessages() {
    setError(null)
    setShowLinkFallback(false)
    setIsUnknownEmail(false)
  }

  /** Step 1: we don't know yet whether this email has a password — or an account. */
  async function onEmailSubmit(e: any) {
    e.preventDefault()
    e.stopPropagation()

    if (isLoading) {
      return
    }

    setIsLoading(true)
    resetMessages()

    try {
      const res = await login({ email })

      if (res.data.result === 'password_required') {
        setHasExistingAccount(isSignup)
        setStep('password')
        return
      }
      if (res.data.result === 'login_link_sent') {
        setHasExistingAccount(isSignup)
        setStep('link_sent')
        return
      }

      applyLoginSession(res.data.profile, res.headers['ha-token'])
      navigate('/portal/dashboard')
    } catch (e: any) {
      if (getApiErrorStatus(e) === 404 && getApiErrorCode(e) === 'unknown_email') {
        // On the signup page this is the normal case. On the login page it is
        // not an error we leave the user with — we point at the signup.
        if (isSignup) {
          await createDemoAccount()
        } else {
          setIsUnknownEmail(true)
        }
        return
      }
      if (handleAuthError(e)) {
        return
      }
      console.error('Portal Login Error', e)
      setError(t('demo.invalidEmail'))
    } finally {
      setIsLoading(false)
    }
  }

  /** Step 2: a password exists and the user wants to use it. */
  async function onPasswordSubmit(e: any) {
    e.preventDefault()
    e.stopPropagation()

    if (isLoading) {
      return
    }

    setIsLoading(true)
    resetMessages()

    try {
      const res = await login({ email, password })

      if (res.data.result === 'login_link_sent') {
        setStep('link_sent')
        return
      }
      if (res.data.result === 'password_required') {
        // Should not happen — we did send a password. Keep the user unstuck.
        setError('E-Mail oder Passwort ist falsch.')
        setPassword('')
        return
      }

      applyLoginSession(res.data.profile, res.headers['ha-token'])
      navigate('/portal/dashboard')
    } catch (e: any) {
      setPassword('')
      if (handleAuthError(e)) {
        return
      }
      console.error('Portal Login Error', e)
      setError('Anmeldung fehlgeschlagen. Bitte versuche es erneut.')
    } finally {
      setIsLoading(false)
    }
  }

  /** "Passwort vergessen?" and "Login-Link schicken" are the same call. */
  async function requestLoginLink() {
    if (isLoading) {
      return
    }

    setIsLoading(true)
    resetMessages()

    try {
      await login({ email, send_login_link: true })
      setStep('link_sent')
    } catch (e: any) {
      if (handleAuthError(e)) {
        return
      }
      console.error('Portal Login Link Error', e)
      setError('Der Login-Link konnte nicht verschickt werden. Bitte versuche es erneut.')
    } finally {
      setIsLoading(false)
    }
  }

  /** Signup: the email belongs to no account yet. */
  async function createDemoAccount() {
    try {
      const res = await createProfDemo(email)

      // Race: the account was created in the meantime — the backend sends a
      // login link instead of a second demo.
      if (res.data?.login_link_sent) {
        setHasExistingAccount(true)
        setStep('link_sent')
        return
      }

      const haToken = res.headers['ha-token']
      applyLoginSession(res.data.profile, haToken)

      try {
        trackLead({ email, source: 'portal_demo' })
      } catch (error) {
        captureException(error, 'Tracking Portal Demo user')
      }

      navigate(`/portal/onboarding?_ha=${haToken}`)
    } catch (e: any) {
      console.error('Portal Demo Error', e)
      setError(t('demo.invalidEmail'))
    }
  }

  function backToEmail() {
    setStep('email')
    setPassword('')
    setHasExistingAccount(false)
    resetMessages()
  }

  // Both do the same thing: there is no separate reset flow, the login link
  // *is* the way back in. Named twice because people look for both wordings.
  const linkActions = (
    <div className='flex flex-col items-start gap-1 text-sm'>
      <button type='button' className='underline opacity-70 hover:opacity-100' onClick={requestLoginLink} disabled={isLoading}>
        Passwort vergessen?
      </button>
      <button type='button' className='underline opacity-70 hover:opacity-100' onClick={requestLoginLink} disabled={isLoading}>
        Login-Link per E-Mail schicken
      </button>
      <button type='button' className='underline opacity-70 hover:opacity-100' onClick={backToEmail} disabled={isLoading}>
        Andere E-Mail verwenden
      </button>
    </div>
  )

  const emailQuery = email ? `?email=${encodeURIComponent(email)}` : ''

  // A login link can point at either page and carries the token in `_ha`
  // (consumed by the auth store at module init). Whoever arrives already signed
  // in belongs in the portal, not on a login or signup form.
  if (wasSignedInOnArrival) {
    return <Navigate to='/portal/dashboard' replace />
  }

  const heading = {
    email: isSignup
      ? { title: 'Kostenlos testen', subline: 'Erstelle deinen PicField-Zugang – in unter einer Minute, ohne Kreditkarte.' }
      : { title: 'Anmelden', subline: 'Willkommen zurück. Melde dich mit deiner E-Mail-Adresse an.' },
    password: { title: 'Anmelden', subline: 'Gib dein Passwort ein, um fortzufahren.' },
    link_sent: { title: 'E-Mail ist unterwegs', subline: null as string | null },
  }[step]

  return (
    <>
      <style>{themeStyle}{`:root { --portal-bg: ${PORTAL_BG}; --portal-border: ${PORTAL_BORDER}; }`}</style>
      <div className='min-h-screen bg-[var(--portal-bg)] flex items-center justify-center p-4'>
        <div className='w-full max-w-lg'>
          <div className='bg-neutral border border-[var(--portal-border)] p-8 md:p-12'>
            <p className='text-xs uppercase tracking-[0.25em] opacity-50 mb-3'>PicField</p>
            <h1 className='text-3xl md:text-4xl font-serif font-light mb-2'>{heading.title}</h1>
            {heading.subline && <p className='opacity-70 mb-8'>{heading.subline}</p>}

            {step === 'link_sent' ? (
              <div className='flex flex-col gap-6'>
                {hasExistingAccount && (
                  <div className='rounded-lg bg-secondary px-4 py-3 text-sm'>
                    Zu <span className='font-medium'>{email}</span> gibt es bereits ein Konto – du musst dich nicht neu registrieren.
                  </div>
                )}
                <div className='rounded-lg bg-success bg-opacity-10 border border-success px-4 py-3 text-success text-sm'>
                  Wir haben dir einen Login-Link an <span className='font-medium'>{email}</span> geschickt – bitte schau in dein Postfach.
                </div>
                <button type='button' className='text-sm underline opacity-70 hover:opacity-100 self-start' onClick={backToEmail}>
                  Andere E-Mail verwenden
                </button>
              </div>
            ) : step === 'password' ? (
              <form onSubmit={onPasswordSubmit} className='flex flex-col gap-6'>
                {hasExistingAccount && (
                  <div className='rounded-lg bg-secondary px-4 py-3 text-sm'>
                    Zu dieser E-Mail-Adresse gibt es bereits ein Konto. Melde dich einfach an.
                  </div>
                )}

                <label className='form-control w-full'>
                  <div className='label'>
                    <span className='label-text'>E-Mail</span>
                  </div>
                  <Input type='email' value={email} disabled />
                </label>

                <label className='form-control w-full'>
                  <div className='label'>
                    <span className='label-text'>Passwort</span>
                  </div>
                  <Input
                    type='password'
                    autoComplete='current-password'
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoFocus
                  />
                </label>

                {error && <p className='text-error text-sm'>{error}</p>}

                <div className='flex justify-end'>
                  <Button color='accent' loading={isLoading}>
                    Anmelden
                  </Button>
                </div>

                {linkActions}
              </form>
            ) : (
              <form onSubmit={onEmailSubmit} className='flex flex-col gap-6'>
                <label className='form-control w-full'>
                  <div className='label'>
                    <span className='label-text'>E-Mail</span>
                  </div>
                  <Input
                    placeholder={t('common.yourEmail')}
                    required
                    type='email'
                    autoComplete='email'
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoFocus
                  />
                </label>

                {isUnknownEmail && (
                  <div className='rounded-lg bg-secondary px-4 py-3 text-sm flex flex-col items-start gap-2'>
                    <span>Zu dieser E-Mail-Adresse gibt es noch kein Konto.</span>
                    <Link to={`${PORTAL_SIGNUP_PATH}${emailQuery}`} className='underline font-medium'>
                      Jetzt kostenlos testen
                    </Link>
                  </div>
                )}

                {error && <p className='text-error text-sm'>{error}</p>}

                {showLinkFallback && (
                  <button type='button' className='text-sm underline opacity-70 hover:opacity-100 self-start' onClick={requestLoginLink} disabled={isLoading}>
                    Login-Link per E-Mail schicken
                  </button>
                )}

                <div className='flex justify-end'>
                  <Button color='accent' loading={isLoading}>
                    {isSignup ? 'Kostenlos testen' : 'Weiter'}
                  </Button>
                </div>
              </form>
            )}

            {isSignup && (
              <p className='text-sm opacity-50 mt-6 max-w-[20rem]'>
                <Trans i18nKey='activate.registrationAgreement'>
                  <a className='underline' href='https://fridaysnap.de/datenschutzerklaerung/' />
                </Trans>
              </p>
            )}

            {/* The way over to the other page — always visible, on every step. */}
            <div className='mt-8 pt-6 border-t border-[var(--portal-border)] text-sm opacity-80'>
              {isSignup ? (
                <>
                  Du hast schon ein Konto?{' '}
                  <Link to={`${PORTAL_LOGIN_PATH}${emailQuery}`} className='underline font-medium'>Anmelden</Link>
                </>
              ) : (
                <>
                  Noch kein Konto?{' '}
                  <Link to={`${PORTAL_SIGNUP_PATH}${emailQuery}`} className='underline font-medium'>Kostenlos testen</Link>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
